/** @jsxImportSource @emotion/react */
import styled from "@emotion/styled";
import { useTokenListByChain } from "hooks/useTokenListByChain";
import React, { useEffect, useMemo, useRef } from "react";
import { useBalanceForSymbol } from "../../hooks/useBalanceForSymbol";
import { GlobalStore } from "../../store/global/GlobalStore";
import { Amount } from "../../types/amount";
import { getPriceForTokenByTokenList } from "../../utils/tokenListUtils";
import { BalanceDetails } from "./sections/BalanceDetails";
import { TokenInputSection } from "./sections/TokenInputSection";
import { useDisplayOnClick } from "hooks/useDisplayOnClick";
import { AnimateChangeInHeight } from "components/general/AnimateChangeInHeight";
import { canLock, handleBalanceOption } from "utils/tradeUtils";
import { Text } from "components/general/Text";
import { TradeLegMakerSlider } from "./sections/TradeLegMakerSlider";
import { AmountAndUsdPriceFullWidth } from "./sections/elements/AmountAndUsdPriceFullWidth";
import { isWrapTrade } from "utils/orderTypeUtils";
import { LockButton } from "./sections/LockButton";
import { BalancePercentageOptionsEnum } from "./sections/BalancePercentages";
import { useGetMaxAmountToWrap } from "../../hooks/wrap/useGetMaxAmountToWrap";
import { AmountLabel, getLabelType } from "./sections/AmountLabel";
import { InteractionStore } from "store/interactions/InteractionStore";
import { Hash } from "viem";
import { isAddressEqual } from "../../utils/tokenUtils";
import { useOnClickOutside } from "usehooks-ts";
import { useTokenlistMetadataByChain } from "../../hooks/queries/tokens-queries";

export type CounterParty = "maker" | "taker";

export interface TradeLegMakerProps {
  tokenAddress: Hash;
  showPlusButton: boolean;
  setIsWaitingToAddToken: React.Dispatch<React.SetStateAction<boolean>>;
  isWaitingToAddToken: boolean;
  setIsChangingToken: React.Dispatch<React.SetStateAction<Hash | undefined>>;
}

export const TradeLegMaker: React.FC<TradeLegMakerProps> = ({
  tokenAddress,
  showPlusButton,
  isWaitingToAddToken,
  setIsWaitingToAddToken,
  setIsChangingToken,
}) => {
  const tokenList = useTokenListByChain();
  const {
    quote,
    network,
    isSending,
    isSliderMoving,
    isRequestingQuote,
    makerTokens,
    takerTokens,
    theme,
  } = GlobalStore.useState(s => ({
    quote: s.quote,
    network: s.network,
    isSending: s.isSending,
    isSliderMoving: s.isSliderMoving,
    isRequestingQuote: s.isRequestingQuote,
    makerTokens: s.makerTokens,
    takerTokens: s.takerTokens,
    theme: s.theme,
  }));
  const { data: tokenListMetadata } = useTokenlistMetadataByChain({ chainId: network.id });
  const balance = useBalanceForSymbol(tokenAddress);
  const getMaxAmountToWrap = useGetMaxAmountToWrap();
  const selectedToken = makerTokens.find(entry => isAddressEqual(entry.tokenAddress, tokenAddress));
  const tokenListPrice = getPriceForTokenByTokenList(tokenAddress, tokenList);
  const quotePrice = quote?.makerTokens[tokenAddress]?.priceUsd;
  const price = quotePrice ?? tokenListPrice;
  const tradeLegToOpenOnAmountBarClick = InteractionStore.useState(
    s => s.tradeLegToOpenOnAmountBarClick
  );

  const tradeLefRef = useRef<HTMLDivElement>(null);

  const {
    isDisplayed: isInputFocused,
    toggleDisplay: onTradeLegClick,
    elementToClickRef: tradeLegRef,
    elementToDisplayRef: sliderRef,
    forceOpen: openBalancePercentages,
    forceClose: hideBalancePercentages,
  } = useDisplayOnClick();
  useOnClickOutside(tradeLefRef, hideBalancePercentages);

  useEffect(() => {
    if (tradeLegToOpenOnAmountBarClick === tokenAddress) {
      openBalancePercentages();
      InteractionStore.update(s => (s.tradeLegToOpenOnAmountBarClick = undefined));
    }
  }, [openBalancePercentages, tokenAddress, tradeLegToOpenOnAmountBarClick]);

  const pct =
    GlobalStore.useState(
      s => s.makerTokens.find(entry => isAddressEqual(entry.tokenAddress, tokenAddress))?.pct
    ) ?? 1;
  const is12M = makerTokens.length > 1;
  const amount = useMemo(() => {
    if (isWrapTrade(takerTokens, makerTokens, network)) return takerTokens[0].amount;
    return (
      (selectedToken?.tokenAddress != null
        ? quote?.makerTokens[selectedToken.tokenAddress]?.amountBeforeFee ?? Amount.ZERO
        : Amount.ZERO) ?? Amount.ZERO
    );
  }, [makerTokens, network, quote?.makerTokens, selectedToken?.tokenAddress, takerTokens]);

  if (tokenList == null || selectedToken == null) return null;

  const color = tokenList?.[tokenAddress]?.colour ?? theme.font.primary.default;
  const decimals = tokenList?.[tokenAddress]?.chainInfo.decimals;

  return (
    <Container ref={tradeLefRef}>
      <TradeRowWrapper
        ref={tradeLegRef as React.MutableRefObject<HTMLDivElement>}
        data-testid={"trade-leg"}
        showSlider={isInputFocused && is12M}
        counterParty={"maker"}
        heightAddOn={24}
        onClick={() => {
          GlobalStore.update(s => {
            s.orderError = undefined;
          });
          if (isInputFocused) return;
          onTradeLegClick();
        }}
      >
        <TokenInputAndBalanceContainer>
          <MainSection>
            <TokenInputSection
              tokenAddress={tokenAddress}
              showPlusButton={showPlusButton}
              isWaitingToAddToken={isWaitingToAddToken}
              setIsWaitingToAddToken={setIsWaitingToAddToken}
              setIsChangingToken={setIsChangingToken}
              hideBalancePercentages={hideBalancePercentages}
              tokenList={tokenList}
              chain={network}
              tokenListMetadata={tokenListMetadata}
            />
            <AmountLabel
              amountToDisplay={amount.value.gt(0) ? amount.toDisplay(tokenAddress, tokenList) : ""}
              labelType={getLabelType(amount)}
              isVisible={!is12M}
            />
            {is12M && (
              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                {makerTokens.length > 2 && selectedToken.isLocked && !isInputFocused && (
                  <LockButton
                    tokenAddress={tokenAddress}
                    canLock={canLock("maker", makerTokens)}
                    isLocked={selectedToken?.isLocked}
                    size={20}
                    iconSize={16}
                  />
                )}
                {!isInputFocused && <PercentageDisplay pct={pct} color={color} />}
              </div>
            )}
          </MainSection>
          <SubSection>
            <BalanceDetails
              tokenAddress={tokenAddress}
              balance={{ tokenAddress, balance: balance?.balance ?? Amount.ZERO }}
              price={price ? price : Amount.ZERO}
              amount={amount}
              onBalanceClick={() => {
                void handleBalanceOption(
                  tokenAddress,
                  "maker",
                  BalancePercentageOptionsEnum.Max,
                  network,
                  { tokenAddress, balance: balance?.balance ?? Amount.ZERO },
                  getMaxAmountToWrap,
                  decimals
                );
              }}
              isBalanceShown={!isSending}
              isAmountShown={is12M && !isInputFocused}
              isUsdValueShown={is12M ? !isInputFocused : true}
            />
          </SubSection>
        </TokenInputAndBalanceContainer>
        {isInputFocused && is12M && (
          <>
            <SliderContainer>
              <TradeLegMakerSlider
                ref={sliderRef as React.Ref<HTMLDivElement>}
                tokenAddress={tokenAddress}
                color={color}
              />
              {makerTokens.length > 2 && (
                <LockButton
                  tokenAddress={tokenAddress}
                  canLock={canLock("maker", makerTokens)}
                  isLocked={selectedToken?.isLocked}
                  size={36}
                />
              )}
            </SliderContainer>
            <AmountAndUsdPriceFullWidth
              tokenAddress={tokenAddress}
              amount={amount}
              usdValue={price.mul(amount).value}
              isAmountShown={true}
              dimAmountValues={isSliderMoving && !isRequestingQuote}
            />
          </>
        )}
      </TradeRowWrapper>
    </Container>
  );
};

// Sub components

const Container = styled.div(() => {
  return { padding: "0px 10px", width: "100%" };
});

const SliderContainer = styled.div(() => {
  return {
    position: "relative",
    top: -22,
    display: "flex",
    flexDirection: "column",
    gap: 6,
    alignItems: "center",
  };
});

const TradeRowWrapper = styled(AnimateChangeInHeight)<{
  showSlider: boolean;
  counterParty: CounterParty;
}>`
  display: flex;
  width: 100%;
  flex-direction: column;
  gap: 4px;
  border-radius: 24px;
  padding: 14px 16px;
  overflow: hidden;
  background: ${props => (props.showSlider ? props.theme.surface.main.tertiary.default : "none")};
`;

const TokenInputAndBalanceContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
  align-self: stretch;
`;

const MainSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  align-self: stretch;
`;

const SubSection = styled.div(() => {
  return { display: "flex", width: "100%", justifyContent: "space-between" };
});

const PercentageDisplay = ({ pct, color }: { pct: number; color: string }) => {
  return (
    <Text textStyle="Title" size="XL" color={color}>
      {pct}%
    </Text>
  );
};
